import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import type { DetailDescriptionSectionProps } from './types';
import { DESCRIPTION_MAX_HEIGHT_PX } from '../../constants';

import styles from './styles.module.scss';
import { HtmlMarkdownContent } from '@components';

export const DetailDescriptionSection: React.FC<DetailDescriptionSectionProps> = ({
  descriptionContent,
}) => {
  const intl = useIntl();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncatable, setIsTruncatable] = useState(false);

  const updateTruncation = useCallback((): void => {
    const el = contentRef.current;
    if (!el) {
      return;
    }
    const exceedsMax = el.scrollHeight > DESCRIPTION_MAX_HEIGHT_PX;
    setIsTruncatable(exceedsMax);
    if (!exceedsMax) {
      setIsExpanded(false);
    }
  }, []);

  useLayoutEffect(() => {
    updateTruncation();
    const el = contentRef.current;
    if (!el) {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      updateTruncation();
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [descriptionContent, updateTruncation]);

  const handleToggleExpanded = useCallback((): void => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <section className={styles.section} aria-labelledby="experience-heading">
      <div className={styles.body}>
        <div
          ref={contentRef}
          id="experience-body"
          className={`${styles.contentClamp}${
            isTruncatable && !isExpanded ? ` ${styles.contentClampCollapsed}` : ''
          }`}
          style={
            isTruncatable && !isExpanded ? { maxHeight: DESCRIPTION_MAX_HEIGHT_PX } : undefined
          }
        >
          <p className={styles.description}>
            {descriptionContent ? (
              <HtmlMarkdownContent content={descriptionContent} />
            ) : (
              intl.formatMessage({ id: 'certificates.detail.noDescription' })
            )}
          </p>
        </div>
        {isTruncatable && (
          <div className={styles.toggleRow}>
            <button
              type="button"
              className={styles.toggleButton}
              onClick={handleToggleExpanded}
              aria-expanded={isExpanded}
              aria-controls="experience-body"
            >
              {isExpanded
                ? intl.formatMessage({ id: 'certificates.detail.showLess' })
                : intl.formatMessage({ id: 'certificates.detail.showAll' })}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
